/**
 * Notification API Service
 * Handles notification retrieval, updates, and preferences
 */
import { apiClient } from './apiClient';
import {
  Notification,
  NotificationPreferences,
  NotificationBatch
} from '../../types/notification.types';

export const notificationService = {
  // ============================================================================
  // NOTIFICATION RETRIEVAL
  // ============================================================================

  /**
   * Get notifications for current user
   */
  getNotifications: async (params?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
    type?: string;
  }): Promise<NotificationBatch> => {
    const response = await apiClient.get<{
      status: string;
      results: number;
      data: { notifications: Notification[] };
    }>('/notifications', {
      params
    });
    return {
      notifications: response.data.data.notifications,
      total: response.data.results,
      page: params?.page || 1,
      hasMore: response.data.data.notifications.length === (params?.limit || 10)
    } as NotificationBatch;
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get<{
      status: string;
      data: { unreadCount: number };
    }>('/notifications/unread-count');
    return { count: response.data.data.unreadCount };
  },

  /**
   * Get a specific notification by ID
   */
  getNotificationById: async (notificationId: string) => {
    const response = await apiClient.get<{
      status: string;
      data: { notification: Notification };
    }>(`/notifications/${notificationId}`);
    return response.data.data.notification;
  },

  // ============================================================================
  // NOTIFICATION UPDATES
  // ============================================================================

  /**
   * Mark a notification as read
   */
  markAsRead: async (notificationId: string) => {
    const response = await apiClient.patch<{
      status: string;
      data: { notification: Notification };
    }>(`/notifications/${notificationId}/read`);
    return response.data.data;
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async () => {
    const response = await apiClient.patch<{
      status: string;
      data: { updatedCount: number };
    }>('/notifications/mark-all-read');
    return {
      success: response.data.status === 'success',
      updatedCount: response.data.data.updatedCount
    };
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (notificationId: string) => {
    await apiClient.delete<{ status: string; data: null }>(
      `/notifications/${notificationId}`
    );
    return { success: true, message: 'Notification deleted successfully' };
  },

  // NOTE: The following methods are not available in the current backend (OLD system)
  // They are part of the NEW domain system which is not yet mounted
  // Uncomment when migrating to the NEW notification system

  /**
   * Mark a notification as unread (NOT AVAILABLE IN OLD BACKEND)
   */
  // markAsUnread: async (notificationId: string) => {
  //   const response = await apiClient.patch<{ success: boolean; data: { notification: Notification } }>(
  //     `/notifications/${notificationId}/unread`
  //   );
  //   return response.data.data;
  // },

  /**
   * Delete all read notifications (NOT AVAILABLE IN OLD BACKEND)
   */
  // deleteAllRead: async () => {
  //   const response = await apiClient.delete<{ success: boolean; data: { deletedCount: number } }>(
  //     '/notifications/read'
  //   );
  //   return response.data.data;
  // },

  // ============================================================================
  // NOTIFICATION PREFERENCES (LOCAL STORAGE FALLBACK)
  // ============================================================================
  // The NEW domain notification system is not yet mounted in the backend
  // Using localStorage as a temporary solution until migration

  /**
   * Get notification preferences (LOCAL STORAGE FALLBACK)
   */
  getPreferences: async (): Promise<{
    preferences: NotificationPreferences;
  }> => {
    // Try to get from localStorage as fallback
    const stored = localStorage.getItem('notificationPreferences');
    if (stored) {
      return { preferences: JSON.parse(stored) as NotificationPreferences };
    }

    // Return default preferences
    const defaultPreferences: NotificationPreferences = {
      email: {
        enabled: true,
        frequency: 'INSTANT'
      },
      push: {
        enabled: true,
        types: ['FOLLOW', 'LIKE', 'COMMENT', 'TRIP_INVITE']
      },
      quietHours: {
        enabled: false,
        start: '22:00',
        end: '08:00'
      },
      categories: {
        FOLLOW: true,
        LIKE: true,
        COMMENT: true,
        TRIP_INVITE: true,
        TRIP_SHARED: true,
        TRIP_PUBLISHED: true,
        COLLABORATION_REQUEST: true,
        SYSTEM: true
      }
    };

    return { preferences: defaultPreferences };
  },

  /**
   * Update notification preferences (LOCAL STORAGE FALLBACK)
   */
  updatePreferences: async (preferences: Partial<NotificationPreferences>) => {
    // Get current preferences
    const current = await notificationService.getPreferences();
    const updated = { ...current.preferences, ...preferences };

    // Save to localStorage
    localStorage.setItem('notificationPreferences', JSON.stringify(updated));

    return { preferences: updated };
  }

  /**
   * Test notification delivery (NOT AVAILABLE IN OLD BACKEND)
   */
  // sendTestNotification: async () => {
  //   const response = await apiClient.post<{ success: boolean; data: { notification: Notification } }>(
  //     '/notifications/test'
  //   );
  //   return response.data.data;
  // },
};
